import { loadAsset, loadFont } from './assets.js';
import { OBJECT_TYPE_DRAWING, OBJECT_TYPE_IMAGE, OBJECT_TYPE_TEXT, OBJECT_TYPE_SHAPE } from './constants.js';
import { readFileAsArrayBuffer } from './fileReaders.js';
import { noop } from './utils.js';

export async function savePdf({ sourceFile, pageObjects, outputFileName, saveAsPng, zipPngs, pagesToExport }) {
  const [PDFLib, download, makeTextPDF, JSZip] = await Promise.all([
    loadAsset('PDFLib'),
    loadAsset('download'),
    loadAsset('makeTextPDF'),
    loadAsset('JSZip'),
  ]);

  let pdfDoc;
  try {
    pdfDoc = await PDFLib.PDFDocument.load(await readFileAsArrayBuffer(sourceFile));
  } catch (e) {
    // Removed console.log
    throw e;
  }

  const pages = pdfDoc.getPages();

  await Promise.all(
    pages.map(async (page, pageIndex) => {
      if (pagesToExport && !pagesToExport.includes(pageIndex)) return;

      const objects = pageObjects && Array.isArray(pageObjects[pageIndex]) ? pageObjects[pageIndex] : [];
      const pageHeight = page.getHeight();

      const drawTasks = await Promise.all(
        objects.map((object) =>
          createDrawTask({
            PDFLib,
            pdfDoc,
            page,
            pageHeight,
            makeTextPDF,
            object,
          }),
        ),
      );

      drawTasks.forEach((draw) => draw());
    }),
  );

  if (pagesToExport) {
    for (let i = pages.length - 1; i >= 0; i--) {
      if (!pagesToExport.includes(i)) {
        pdfDoc.removePage(i);
      }
    }
  }

  try {
    const pdfBytes = await pdfDoc.save();
    const parsedOutputName = outputFileName.replace(/\.(pdf|zip|png)$/i, "") || 'document';

    if (saveAsPng) {
      const pdfjsLib = await loadAsset('pdfjsLib');
      const renderDoc = await pdfjsLib.getDocument({ data: pdfBytes }).promise;
      const numPages = renderDoc.numPages;

      if (zipPngs) {
        const zip = new JSZip();
        for (let i = 1; i <= numPages; i++) {
          const page = await renderDoc.getPage(i);
          const viewport = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({ canvasContext: context, viewport }).promise;
          const imgData = canvas.toDataURL("image/png").split(',')[1];
          zip.file(`${parsedOutputName}_Page${String(i).padStart(3, '0')}.png`, imgData, { base64: true });
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        download(zipBlob, `${parsedOutputName}.zip`, 'application/zip');
      } else {
        for (let i = 1; i <= numPages; i++) {
          const page = await renderDoc.getPage(i);
          const viewport = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({ canvasContext: context, viewport }).promise;
          const imgUrl = canvas.toDataURL("image/png");

          let dlFileName = parsedOutputName;
          if (numPages > 1) {
            dlFileName = `${parsedOutputName}_Page${String(i).padStart(3, '0')}`;
          }
          download(imgUrl, `${dlFileName}.png`, 'image/png');
        }
      }
    } else {
      download(pdfBytes, `${parsedOutputName}.pdf`, 'application/pdf');
    }
  } catch (e) {
    // Removed console.log
    throw e;
  }
}

async function createDrawTask({ PDFLib, pdfDoc, page, pageHeight, makeTextPDF, object }) {
  switch (object && object.type) {
    case OBJECT_TYPE_IMAGE:
      return createImageTask({ pdfDoc, page, pageHeight, object });
    case OBJECT_TYPE_TEXT:
      return createTextTask({ pdfDoc, page, pageHeight, makeTextPDF, object });
    case OBJECT_TYPE_DRAWING:
      return createDrawingTask({ PDFLib, page, pageHeight, object });
    case OBJECT_TYPE_SHAPE:
      return createShapeTask({ PDFLib, page, pageHeight, object });
    default:
      return noop;
  }
}

async function createImageTask({ pdfDoc, page, pageHeight, object }) {
  const { imageFile, x, y, width, height } = object;
  if (!imageFile) return noop;

  try {
    const fileType = imageFile.type;
    const imageBytes = await readFileAsArrayBuffer(imageFile);
    const embeddedImage =
      fileType === 'image/jpeg' ? await pdfDoc.embedJpg(imageBytes) : await pdfDoc.embedPng(imageBytes);

    return () =>
      page.drawImage(embeddedImage, {
        x,
        y: pageHeight - y - height,
        width,
        height,
      });
  } catch (e) {
    // Removed console.log
    return noop;
  }
}

async function createTextTask({ pdfDoc, page, pageHeight, makeTextPDF, object }) {
  const { x, y, lines, lineHeight, fontSize, fontFamily, width, scaleX = 1, scaleY = 1 } = object;
  if (!Array.isArray(lines) || lines.length === 0) return noop;

  const height = fontSize * lineHeight * lines.length;
  let font;
  try {
    font = await loadFont(fontFamily);
  } catch (e) {
    font = await loadFont('標楷體');
  }

  const [textPage] = await pdfDoc.embedPdf(
    await makeTextPDF({
      lines,
      fontSize,
      lineHeight,
      width,
      height,
      font: font.buffer || font.src || 'Helvetica',
      dy: font.correction(fontSize, lineHeight),
    }),
  );

  return () =>
    page.drawPage(textPage, {
      width: width * scaleX,
      height: height * scaleY,
      x,
      y: pageHeight - y - height * scaleY,
    });
}

function createDrawingTask({ PDFLib, page, pageHeight, object }) {
  const { x, y, svgPath, scale } = object;
  const {
    LineCapStyle,
    LineJoinStyle,
    popGraphicsState,
    pushGraphicsState,
    setLineCap,
    setLineJoin,
  } = PDFLib;

  return () => {
    page.pushOperators(
      pushGraphicsState(),
      setLineCap(LineCapStyle.Round),
      setLineJoin(LineJoinStyle.Round),
    );
    page.drawSvgPath(svgPath, {
      borderWidth: 5,
      scale,
      x,
      y: pageHeight - y,
    });
    page.pushOperators(popGraphicsState());
  };
}

function hexToRgb(PDFLib, hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return PDFLib.rgb(r, g, b);
}

function createShapeTask({ PDFLib, page, pageHeight, object }) {
  const { x, y, width, height, shapeType, fillColor, strokeColor, strokeWidth } = object;
  const { LineCapStyle, LineJoinStyle, pushGraphicsState, popGraphicsState, setLineCap, setLineJoin } = PDFLib;

  return () => {
    const sw = strokeWidth || 0;
    const fColor = fillColor !== 'transparent' ? hexToRgb(PDFLib, fillColor) : undefined;
    const sColor = strokeColor !== 'transparent' && sw > 0 ? hexToRgb(PDFLib, strokeColor) : undefined;
    const rectX = x;
    const rectY = pageHeight - y - height;

    if (shapeType === 'rect') {
      page.drawRectangle({
        x: rectX + sw / 2,
        y: rectY + sw / 2,
        width: Math.max(0, width - sw),
        height: Math.max(0, height - sw),
        color: fColor,
        borderColor: sColor,
        borderWidth: sw,
      });
    } else if (shapeType === 'circle') {
      page.drawEllipse({
        x: x + width / 2,
        y: pageHeight - y - height / 2,
        xScale: Math.max(0, width / 2 - sw / 2),
        yScale: Math.max(0, height / 2 - sw / 2),
        color: fColor,
        borderColor: sColor,
        borderWidth: sw,
      });
    } else {
      page.pushOperators(
        pushGraphicsState(),
        setLineCap(LineCapStyle.Round),
        setLineJoin(LineJoinStyle.Round),
      );

      let svgPath = '';
      if (shapeType === 'x') {
        const t = Math.min(width, height) * 0.15;
        const tx = t;
        const ty = t * (height / width);
        const left = sw, right = width - sw, top = sw, bottom = height - sw;
        const cx = width / 2, cy = height / 2;
        const pts = [
          `${left},${top + ty}`, `${cx - tx},${cy}`, `${left},${bottom - ty}`,
          `${left + tx},${bottom}`, `${cx},${cy + ty}`, `${right - tx},${bottom}`,
          `${right},${bottom - ty}`, `${cx + tx},${cy}`, `${right},${top + ty}`,
          `${right - tx},${top}`, `${cx},${cy - ty}`, `${left + tx},${top}`
        ];
        svgPath = `M ${pts[0]} L ${pts.slice(1).join(' L ')} Z`;
      } else if (shapeType === 'star') {
        const cx = width / 2;
        const cy = height / 2;
        const outerRadius = Math.min(width, height) / 2 - sw;
        const innerRadius = outerRadius / 2;
        let points = [];
        for (let i = 0; i < 10; i++) {
          const radius = i % 2 === 0 ? outerRadius : innerRadius;
          const angle = (i * Math.PI) / 5 - Math.PI / 2;
          points.push(`${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`);
        }
        svgPath = `M ${points[0]} L ${points.slice(1).join(' L ')} Z`;
      } else if (shapeType === 'line') {
        const rectH = Math.max(0, Math.min(width, height) * 0.2);
        const rectW = Math.max(0, width - sw);
        const rectX = sw / 2;
        const rectY = height / 2 - rectH / 2;
        const rx = rectH / 2;
        svgPath = `M ${rectX + rx} ${rectY} L ${rectX + rectW - rx} ${rectY} A ${rx} ${rx} 0 0 1 ${rectX + rectW} ${rectY + rx} A ${rx} ${rx} 0 0 1 ${rectX + rectW - rx} ${rectY + rectH} L ${rectX + rx} ${rectY + rectH} A ${rx} ${rx} 0 0 1 ${rectX} ${rectY + rx} A ${rx} ${rx} 0 0 1 ${rectX + rx} ${rectY} Z`;
      }

      if (svgPath) {
        page.drawSvgPath(svgPath, {
          x,
          y: pageHeight - y,
          color: fColor,
          borderColor: sColor,
          borderWidth: sw,
          scale: 1,
        });
      }

      page.pushOperators(popGraphicsState());
    }
  };
}
